"use client"

import styled from "@emotion/styled"
import { useLocale } from "next-intl"
import Image from "next/image"
import { useEffect, useRef, useState } from "react"
import type { Swiper as SwiperType } from "swiper"
import "swiper/css"
import { Swiper, SwiperSlide } from "swiper/react"
import { LeftArrow, RightArrow } from "ui"
import {
  extractProperty,
  getLinkHrefFromMultiUrlPickerItem,
  getUmbracoMediaUrl,
  replaceRichTextLinks,
} from "utils"
import { LANDING_ELEMENTS, useLandingAnalytics } from "../analytics"
import { JsonLd, buildReviewSchema } from "utils/structuredData"
import { BRUSH_UNDERLINE_URI } from "../brushUnderline"
import { handleAnchorScroll } from "../scrollToAnchor"

type Props = {
  data: any
  /* Page title, for the schema's itemReviewed. A block only gets its own data,
     so MarketingLandingPage passes it down (ASH-2196). */
  itemName?: string
}

// --- Viewport entrance hook (fires once per page load) ---

function useInViewOnce<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || inView) return
    const o = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true)
          o.disconnect()
        }
      },
      { threshold: 0.2 },
    )
    o.observe(el)
    return () => o.disconnect()
  }, [inView])
  return { ref, inView }
}

// --- Styled Components ---

const Section = styled.section`
  padding: 40px 0;
  background-color: var(--comp-bg);
  color: var(--comp-text);
  overflow: hidden;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 90px 0;
  }
`

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`

const Header = styled.div`
  text-align: center;
  margin-bottom: 32px;

  ${(props) => props.theme.breakpoints.up("md")} {
    margin-bottom: 48px;
  }
`

const Headline = styled.h2`
  font-size: 32px;
  font-weight: 500;
  line-height: 1.2;
  color: var(--comp-heading);
  margin: 0;

  /* RTE headline arrives wrapped in <p>; keep it inline so the h2 flows normally */
  p {
    margin: 0;
    display: inline;
  }
  /* Accent words are marked bold in the CMS. Rendered with the brush-underline
     SVG as a CSS mask so the colour follows currentColor (ASH-1549). */
  strong {
    text-decoration: none;
    display: inline-block;
    margin: 0;
    position: relative;
    font-weight: 700;
    &::after {
      content: "";
      position: absolute;
      bottom: -4px;
      inset-inline: 0;
      height: 6px;
      z-index: 1;
      background-color: currentColor;
      -webkit-mask: url("${BRUSH_UNDERLINE_URI}") no-repeat center / 100% 100%;
      mask: url("${BRUSH_UNDERLINE_URI}") no-repeat center / 100% 100%;
    }
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 60px;
  }
`

const HeaderText = styled.p`
  font-size: 24px;
  font-weight: 300;
  line-height: 1.5;
  color: var(--comp-text);
  margin: 16px 0 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    margin-top: 24px;
  }
`

const CarouselWrapper = styled.div`
  position: relative;
  /* Full-bleed: break out of the centered Inner so cards begin at the
     section's start edge and span the full width (ASH-1543). */
  width: 100vw;
  max-width: 100vw;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  padding: 0 20px;
  box-sizing: border-box;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 0 40px;
  }

  .swiper {
    overflow: visible;
  }

  /* Fixed card widths straight from Figma — 300 mobile (1901:9077), 352 on
     desktop at both 1440 (1900:8701) and 1920 (3009:22047). Height stays
     content-driven, which is why the design shows 386 at 1440 and 399 at 1920
     for the same 352 width (ASH-1546). */
  .swiper-slide {
    width: 300px;
    height: auto;

    ${(props) => props.theme.breakpoints.up("md")} {
      width: 352px;
    }
  }

  /* Equal-height slides so card footers align across the row */
  .swiper-wrapper {
    align-items: stretch;
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .swiper-slide {
    height: auto;
    display: flex;
  }
`

const Card = styled.div<{ inView: boolean; index: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
  height: 100%;
  padding: 16px;
  box-sizing: border-box;
  border-radius: 8px;
  border: 1px solid var(--inner-text);
  background-color: var(--inner-bg);
  color: var(--inner-text);
  text-align: center;

  opacity: ${({ inView }) => (inView ? 1 : 0)};
  transform: ${({ inView }) => (inView ? "translateY(0)" : "translateY(40px)")};
  transition: opacity 0.5s ease, transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
  transition-delay: ${({ index }) => index * 80}ms;
  height: 356px;
  ${(props) => props.theme.breakpoints.up("md")} {
    height: 386px;
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    height: 399px;
  }
`

const Stars = styled.div`
  display: flex;
  gap: 4px;
  /* Always reserve the star row so a card without a rating keeps its quote
     aligned with rated cards in the same row (ASH-1534). */
  min-height: 24px;
`

const Quote = styled.p`
  font-size: 16px;
  font-weight: 300;
  line-height: 1.5;
  color: var(--inner-text);
  margin: 0;
  /* Clamp quote text so all cards stay the same height regardless of
     content length (ASH-2129). */
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const CardFooter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  /* Pin the avatar + name + link block to the bottom so cards line up */
  margin-top: auto;
`

const AvatarImage = styled.div`
  position: relative;
  width: 84px;
  height: 84px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
`

const AvatarInitials = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 84px;
  height: 84px;
  border-radius: 50%;
  background-color: var(--inner-heading, var(--inner-text));
  color: var(--inner-bg);
  font-size: 28px;
  font-weight: 700;
  flex-shrink: 0;
`

const ReviewerName = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: var(--inner-heading, var(--inner-text));
`

const ReviewLink = styled.a`
  display: inline-block;
  font-size: 16px;
  font-weight: 400;
  color: var(--inner-text);
  /* Stay within the card's own colour package so the pill is legible on any
     inner background, even a contrasting one (ASH-1544). */
  background-color: transparent;
  border: 1px solid var(--inner-text);
  border-radius: 100px;
  padding: 8px 24px;
  text-decoration: none;
  transition: background-color 0.2s ease, color 0.2s ease;

  &:hover {
    background-color: var(--inner-text);
    color: var(--inner-bg);
  }

  &:focus-visible {
    outline: 2px dashed var(--inner-text);
    outline-offset: 2px;
  }
`

/* Reserve the link's height even when a card has no link, so the avatar + name
   block stays aligned with cards that do have a link (ASH-1534). */
const LinkSlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
`

/* Figma puts the arrow cluster hard against the right edge on mobile (x 232..355
   inside a 375 frame with 20 padding) and centres it on desktop (centre 959.5 of
   1920). It was centred on both (ASH-1548).

   The design is RTL, so "right" here is the reading START — `flex-start`, not
   `flex-end`. Using `flex-end` puts the cluster against the physical left in
   RTL, which is the mirror image of what the ticket asks for. */
const Controls = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 16px;
  margin-top: 24px;

  ${(props) => props.theme.breakpoints.up("md")} {
    justify-content: center;
  }
`

const ArrowBtn = styled.button`
  /* Bare arrow icon — no circle border/background (ASH-1547). */
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 4px;
  color: var(--comp-text);

  svg {
    color: var(--comp-text);
    path {
      fill: var(--comp-text);
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  &:focus-visible {
    outline: 2px dashed var(--comp-text);
    outline-offset: 2px;
  }
`

const PagerDot = styled.button<{ active: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 100px;
  border: ${({ active }) => (active ? "none" : "1px solid var(--comp-text)")};
  background-color: ${({ active }) => (active ? "var(--comp-text)" : "transparent")};
  padding: 0;
  cursor: pointer;

  &:focus-visible {
    outline: 2px dashed var(--comp-text);
    outline-offset: 2px;
  }
`

// --- Star icon ---

function Star({ filled }: { filled: boolean }) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden>
      <path
        d="M12 2l2.94 6.26L21.5 9.27l-4.75 4.35L17.88 20 12 16.62 6.12 20l1.13-6.38L2.5 9.27l6.56-1.01L12 2z"
        fill="currentColor"
        opacity={filled ? 1 : 0.25}
      />
    </svg>
  )
}

// --- Types & parsing ---

type ReviewItem = {
  reviewerName: string
  quote: string
  /* full multi-URL-picker item so we can resolve internal/PDF links correctly (ASH-1532) */
  link: { url?: string; name?: string; target?: string; type?: string } | null
  starRating: number
  imageUrl: string
  imageAlt: string
  hide: boolean
}

function parseItem(block: any): ReviewItem {
  const link = extractProperty(block, "link") as any[] | ""
  const image = extractProperty(block, "profileImage", true) as any[] | undefined
  const rating = parseInt((extractProperty(block, "starRating") as string) || "", 10)

  return {
    reviewerName: (extractProperty(block, "reviewerName") as string) || "",
    quote: (extractProperty(block, "quote") as string) || "",
    link: (Array.isArray(link) && link[0]) || null,
    starRating: Number.isFinite(rating) ? Math.min(Math.max(rating, 0), 5) : 0,
    imageUrl: image?.[0]?.url || "",
    imageAlt: image?.[0]?.altText || "",
    hide: extractProperty(block, "hide") === true,
  }
}

function isEmptyItem(item: ReviewItem): boolean {
  return !item.reviewerName && !item.quote
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
}

// --- Main Component ---

function Reviews({ data, itemName = "" }: Props) {
  const locale = useLocale()
  const isRtl = locale === "he" || !locale
  const analytics = useLandingAnalytics()
  const [swiperRef, setSwiperRef] = useState<SwiperType | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [snapCount, setSnapCount] = useState(0)
  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)
  const { ref: carouselRef, inView } = useInViewOnce<HTMLDivElement>()

  const syncSwiperState = (swiper: SwiperType) => {
    setActiveIndex(swiper.activeIndex)
    setSnapCount(swiper.snapGrid?.length || 0)
    setIsBeginning(swiper.isBeginning)
    setIsEnd(swiper.isEnd)
  }

  const headline = extractProperty(data, "headline") as string | ""
  const text = extractProperty(data, "text") as string | ""
  const itemBlocks = extractProperty(data, "items") as any[] | ""
  const items = (Array.isArray(itemBlocks) ? itemBlocks : [])
    .map(parseItem)
    .filter((item) => !item.hide && !isEmptyItem(item))

  if (!headline && items.length === 0) return null

  const reviewSchema = buildReviewSchema(items, itemName)

  return (
    <Section>
      <JsonLd schema={reviewSchema} />
      <Inner>
        <Header>
          {headline && (
            <Headline
              dangerouslySetInnerHTML={{
                __html: replaceRichTextLinks(headline, true),
              }}
            />
          )}
          {text && <HeaderText>{text}</HeaderText>}
        </Header>
        {items.length > 0 && (
          <CarouselWrapper ref={carouselRef}>
            <Swiper
              onSwiper={(swiper) => {
                setSwiperRef(swiper)
                syncSwiperState(swiper)
              }}
              onSlideChange={syncSwiperState}
              onResize={syncSwiperState}
              dir={isRtl ? "rtl" : "ltr"}
              /* Card width is fixed by CSS rather than derived from a per-
                 breakpoint slide count. Counting slides made the card scale
                 with the viewport — at 1920 five slides across 100vw came out
                 at 371px against a spec of 352 (ASH-1546). */
              spaceBetween={24}
              slidesPerView="auto"
              wrapperTag="ul"
            >
              {items.map((item, i) => (
                <SwiperSlide key={i} tag="li" style={{ height: "auto" }}>
                  <Card inView={inView} index={i}>
                    {/* Star row is always rendered (reserves height) so cards with and
                        without a rating keep their quotes aligned (ASH-1534). */}
                    <Stars
                      role={item.starRating > 0 ? "img" : undefined}
                      aria-label={item.starRating > 0 ? `${item.starRating}/5` : undefined}
                      aria-hidden={item.starRating === 0}
                    >
                      {item.starRating > 0 &&
                        [1, 2, 3, 4, 5].map((star) => (
                          <Star key={star} filled={star <= item.starRating} />
                        ))}
                    </Stars>
                    {item.quote && <Quote>{item.quote}</Quote>}
                    <CardFooter>
                      {item.imageUrl ? (
                        <AvatarImage>
                          <Image
                            src={getUmbracoMediaUrl(item.imageUrl)}
                            fill
                            alt={item.imageAlt || item.reviewerName}
                            style={{ objectFit: "cover" }}
                          />
                        </AvatarImage>
                      ) : (
                        item.reviewerName && (
                          <AvatarInitials>{getInitials(item.reviewerName)}</AvatarInitials>
                        )
                      )}
                      {item.reviewerName && <ReviewerName>{item.reviewerName}</ReviewerName>}
                      {/* Link slot always present so avatar+name align whether or not a link exists (ASH-1534) */}
                      <LinkSlot>
                        {item.link?.url && item.link?.name && (
                          <ReviewLink
                            href={getLinkHrefFromMultiUrlPickerItem(item.link as any)}
                            onClick={(e) => {
                              analytics.reviewButtonClick(item.link?.name || "", item.reviewerName)
                              handleAnchorScroll(
                                e,
                                getLinkHrefFromMultiUrlPickerItem(item.link as any),
                              )
                            }}
                            target={item.link.target || undefined}
                            rel={item.link.target === "_blank" ? "noopener noreferrer" : undefined}
                          >
                            {item.link.name}
                          </ReviewLink>
                        )}
                      </LinkSlot>
                    </CardFooter>
                  </Card>
                </SwiperSlide>
              ))}
            </Swiper>
            <Controls>
              <ArrowBtn
                onClick={() => {
                  analytics.carouselNavigation("prev_slide", LANDING_ELEMENTS.reviews)
                  swiperRef?.slidePrev()
                }}
                disabled={isBeginning}
                aria-label={isRtl ? "לביקורת הקודמת" : "Previous review"}
              >
                {isRtl ? <RightArrow fill="currentColor" /> : <LeftArrow fill="currentColor" />}
              </ArrowBtn>
              {Array.from({ length: snapCount }, (_, i) => (
                <PagerDot
                  key={i}
                  active={i === activeIndex}
                  onClick={() => {
                    analytics.carouselNavigation(`slide_${i + 1}`, LANDING_ELEMENTS.reviews)
                    swiperRef?.slideTo(i)
                  }}
                  aria-label={isRtl ? `עבור לביקורת ${i + 1}` : `Go to review ${i + 1}`}
                  aria-current={i === activeIndex ? "true" : undefined}
                />
              ))}
              <ArrowBtn
                onClick={() => {
                  analytics.carouselNavigation("next_slide", LANDING_ELEMENTS.reviews)
                  swiperRef?.slideNext()
                }}
                disabled={isEnd}
                aria-label={isRtl ? "לביקורת הבאה" : "Next review"}
              >
                {isRtl ? <LeftArrow fill="currentColor" /> : <RightArrow fill="currentColor" />}
              </ArrowBtn>
            </Controls>
          </CarouselWrapper>
        )}
      </Inner>
    </Section>
  )
}

export default Reviews
